<template>
  <Dialog v-model="dialogVisible" title="修改上级推广人" width="500">
    <el-form
      ref="formRef"
      v-loading="formLoading"
      :model="formData"
      :rules="formRules"
      label-width="80px"
    >
      <el-form-item label="推广人" prop="bindUserId">
        <el-select
          v-model="formData.bindUserId"
          filterable
          remote
          reserve-keyword
          class="w-1/1"
          placeholder="请输入推广员编号 / 昵称搜索"
          :remote-method="debouncedSearchBindUser"
          :loading="searchLoading"
          @change="handleBindUserSelected"
        >
          <el-option
            v-for="item in bindUserSearchOptions"
            :key="item.id"
            :label="`${item.id} - ${item.nickname || '未命名'}`"
            :value="item.id"
          />
        </el-select>
      </el-form-item>
    </el-form>
    <!-- 展示上级推广人的信息 -->
    <el-descriptions v-if="bindUser" :column="1" border>
      <el-descriptions-item label="头像">
        <el-avatar :src="bindUser.avatar" />
      </el-descriptions-item>
      <el-descriptions-item label="昵称">{{ bindUser.nickname }}</el-descriptions-item>
      <el-descriptions-item label="推广资格">
        <el-tag v-if="bindUser.brokerageEnabled">有</el-tag>
        <el-tag v-else type="info">无</el-tag>
      </el-descriptions-item>
      <el-descriptions-item label="成为推广员的时间">
        {{ formatDate(bindUser.brokerageTime) }}
      </el-descriptions-item>
    </el-descriptions>
    <template #footer>
      <el-button :disabled="formLoading" type="primary" @click="submitForm">确 定</el-button>
      <el-button @click="dialogVisible = false">取 消</el-button>
    </template>
  </Dialog>
</template>
<script lang="ts" setup>
import * as BrokerageUserApi from '@/api/mall/trade/brokerage/user'
import { formatDate } from '@/utils/formatTime'

/** 修改分销用户 */
defineOptions({ name: 'BrokerageUserUpdateForm' })

const { t } = useI18n() // 国际化
const message = useMessage() // 消息弹窗

const dialogVisible = ref(false) // 弹窗的是否展示
const formLoading = ref(false) // 表单的加载中：1）修改时的数据加载；2）提交的按钮禁用
const searchLoading = ref(false) // 搜索推广员的独立 loading
const formData = ref()
const formRef = ref() // 表单 Ref
const formRules = reactive({
  bindUserId: [{ required: true, message: '推广员人不能为空', trigger: 'change' }]
})

const bindUserSearchOptions = ref<BrokerageUserApi.BrokerageUserVO[]>([])
let bindUserSearchTimer: ReturnType<typeof setTimeout> | null = null

const debouncedSearchBindUser = (query: string) => {
  if (bindUserSearchTimer) clearTimeout(bindUserSearchTimer)
  bindUserSearchTimer = setTimeout(() => searchBindUserOptions(query), 300)
}

const searchBindUserOptions = async (query: string) => {
  const keyword = String(query || '').trim()
  if (!keyword) {
    bindUserSearchOptions.value = []
    return
  }
  searchLoading.value = true
  try {
    const options: BrokerageUserApi.BrokerageUserVO[] = []
    if (/^\d+$/.test(keyword)) {
      try {
        const user = await BrokerageUserApi.getBrokerageUser(Number(keyword))
        if (user) options.push(user)
      } catch (e: any) {
        // 编号精确查找失败不影响昵称模糊搜索
      }
    }
    const page = await BrokerageUserApi.getBrokerageUserPage({
      nickname: keyword,
      pageNo: 1,
      pageSize: 20
    })
    ;(page.list || []).forEach((item) => {
      if (!options.some((o) => o.id === item.id)) options.push(item)
    })
    bindUserSearchOptions.value = options
  } catch (e: any) {
    bindUserSearchOptions.value = []
    const errMsg = e?.message || e?.msg || ''
    if (errMsg && !/服务器内部错误/.test(errMsg)) {
      message.error(errMsg)
    } else {
      message.error('搜索推广员失败，请检查输入后重试')
    }
  } finally {
    searchLoading.value = false
  }
}

const loadCurrentBindUser = async (id?: number) => {
  if (!id) return
  searchLoading.value = true
  try {
    const user = await BrokerageUserApi.getBrokerageUser(id)
    if (user) {
      bindUser.value = user
      if (!bindUserSearchOptions.value.some((item) => item.id === user.id)) {
        bindUserSearchOptions.value = [user, ...bindUserSearchOptions.value]
      }
    }
  } finally {
    searchLoading.value = false
  }
}

const handleBindUserSelected = async (id: any) => {
  if (!id) {
    bindUser.value = undefined
    return
  }
  if (id == formData.value?.id) {
    message.error('不能绑定自己为推广人')
    formData.value.bindUserId = undefined
    bindUser.value = undefined
    return
  }
  searchLoading.value = true
  try {
    const user = await BrokerageUserApi.getBrokerageUser(id)
    if (!user) {
      message.warning('推广员不存在')
      formData.value.bindUserId = undefined
      bindUser.value = undefined
      return
    }
    bindUser.value = user
  } finally {
    searchLoading.value = false
  }
}

/** 打开弹窗 */
const open = async (row: BrokerageUserApi.BrokerageUserVO) => {
  resetForm()
  // 设置数据
  formData.value.id = row.id
  formData.value.bindUserId = row.bindUserId
  // 反显上级推广人
  if (row.bindUserId) {
    await loadCurrentBindUser(row.bindUserId)
  }
  dialogVisible.value = true
}
defineExpose({ open }) // 提供 open 方法，用于打开弹窗

/** 提交表单 */
const emit = defineEmits(['success']) // 定义 success 事件，用于操作成功后的回调
/** 修改上级推广人 */
const submitForm = async () => {
  if (formLoading.value) return
  // 校验表单
  if (!formRef) return
  const valid = await formRef.value.validate()
  if (!valid) return
  // 未查找到合适的上级
  if (!bindUser.value) {
    message.error('请先查询并确认推广人')
    return
  }

  // 提交请求
  formLoading.value = true
  try {
    // 发起修改
    await BrokerageUserApi.updateBindUser(formData.value)
    message.success(t('common.updateSuccess'))
    dialogVisible.value = false
    // 发送操作成功的事件
    emit('success', true)
  } catch (e: any) {
    const errMsg = e?.message || e?.msg || ''
    if (errMsg && !/服务器内部错误/.test(errMsg)) {
      message.error(errMsg)
    }
  } finally {
    formLoading.value = false
  }
}

/** 重置表单 */
const resetForm = () => {
  formData.value = {
    id: undefined,
    bindUserId: undefined
  }
  formRef.value?.resetFields()
  bindUser.value = undefined
  bindUserSearchOptions.value = []
  searchLoading.value = false
  if (bindUserSearchTimer) clearTimeout(bindUserSearchTimer)
}

/** 查询推广员 */
const bindUser = ref<BrokerageUserApi.BrokerageUserVO>()
</script>
